import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import { AuthContext } from '../../context/AuthContext';

const Clientes = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Modal Crear / Editar
    const [showModal, setShowModal] = useState(false);
    const [editingCustomerId, setEditingCustomerId] = useState(null);
    const [customerFormData, setCustomerFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        cedula: '',
        address: '',
        city: '',
        customer_type: 'regular',
        is_vip: false,
        is_active: true
    });

    // Modal Detalles
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showModal) closeModal();
                if (showDetailsModal) closeDetailsModal();
            }
        };

        if (showModal || showDetailsModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showModal, showDetailsModal]);

    const fetchCustomers = async (search = '') => {
        setLoading(true);
        setError('');
        try {
            const params = { page_size: 1000 };
            if (search) params.search = search;

            const response = await api.get('/api/customers/admin/list/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params
            });
            setCustomers(response.data.data.customers || response.data.results || []);
        } catch (err) {
            console.error('Error fetching customers:', err);
            setError('Error al cargar clientes: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchCustomers(searchTerm);
    };

    const handleOpenCreate = () => {
        setEditingCustomerId(null);
        setCustomerFormData({
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
            cedula: '',
            address: '',
            city: '',
            customer_type: 'regular',
            is_vip: false,
            is_active: true
        });
        setShowModal(true);
    };

    const handleOpenEdit = (customer) => {
        setEditingCustomerId(customer.id);
        setCustomerFormData({
            email: customer.email || '',
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            phone: customer.phone || '',
            cedula: customer.cedula || '',
            address: customer.address || '',
            city: customer.city || '',
            customer_type: customer.customer_type || 'regular',
            is_vip: !!customer.is_vip,
            is_active: customer.is_active !== undefined ? customer.is_active : true
        });
        if (showDetailsModal) setShowDetailsModal(false);
        setShowModal(true);
    };

    const handleOpenDetails = async (customer) => {
        setSelectedCustomer(customer);
        setShowDetailsModal(true);
        setLoadingDetails(true);
        try {
            const res = await api.get(`/api/customers/admin/${customer.id}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (res.data?.data) {
                setSelectedCustomer(res.data.data);
            }
        } catch (err) {
            console.error('Error cargando detalles del cliente:', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleDeleteCustomer = async (customer) => {
        const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email;
        if (!window.confirm(`¿Estás seguro de que deseas eliminar permanentemente al cliente "${name}"? Esta acción no se puede deshacer.`)) {
            return;
        }

        try {
            await api.delete(`/api/customers/admin/${customer.id}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            alert('Cliente eliminado exitosamente');
            if (showDetailsModal && selectedCustomer?.id === customer.id) {
                setShowDetailsModal(false);
                setSelectedCustomer(null);
            }
            fetchCustomers(searchTerm);
        } catch (err) {
            console.error('Error deleting customer:', err);
            const msg = err.response?.data?.message || err.message || 'Error al eliminar el cliente';
            alert('Error: ' + msg);
        }
    };

    const handleSaveCustomer = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingCustomerId) {
                await api.put(`/api/customers/admin/${editingCustomerId}/`, customerFormData, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                alert('Cliente actualizado exitosamente');
            } else {
                await api.post('/api/customers/register/', {
                    ...customerFormData,
                    password: 'Password123!',
                    password_confirmation: 'Password123!'
                }, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                alert('Cliente creado exitosamente');
            }

            closeModal();
            fetchCustomers(searchTerm);
        } catch (err) {
            console.error('Error saving customer:', err);
            const errorData = err.response?.data;
            let errorMessage = editingCustomerId ? 'Error al actualizar cliente' : 'Error al crear cliente';

            if (errorData?.errors) {
                errorMessage += ':\n' + Object.entries(errorData.errors)
                    .map(([key, val]) => `- ${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                    .join('\n');
            } else if (errorData?.message) {
                errorMessage += ': ' + errorData.message;
            } else if (typeof errorData === 'object') {
                errorMessage += ':\n' + Object.entries(errorData)
                    .map(([key, val]) => `- ${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                    .join('\n');
            } else {
                errorMessage += ': ' + err.message;
            }

            alert(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCustomerFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingCustomerId(null);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedCustomer(null);
    };

    const getCustomerTypeColor = (type) => {
        const colors = {
            'regular': { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
            'premium': { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
            'vip': { bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
            'corporate': { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
            'student': { bg: '#fdf2f8', text: '#be185d', border: '#fbcfe8' },
            'first_time': { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' }
        };
        return colors[type?.toLowerCase()] || { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' };
    };

    if (loading && customers.length === 0) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: 'var(--sidebar-bg)' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e5e7eb',
                        borderTopColor: 'var(--primary-color)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '16px', color: '#4b5563', fontWeight: '500' }}>Cargando clientes...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fb', padding: '24px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#111827', margin: '0 0 4px 0' }}>Gestión de Pacientes y Clientes</h1>
                        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Administra la base de datos de clientes, sus historiales e información de contacto.</p>
                    </div>
                    <button
                        onClick={handleOpenCreate}
                        style={{
                            backgroundColor: '#2563eb',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <i className="bi bi-plus-lg" style={{ fontSize: '16px' }} />
                        Nuevo Cliente
                    </button>
                </div>

                {/* Search Bar */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '24px' }}>
                    <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <i className="bi bi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, cédula/RUC, email o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px 10px 40px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    fontSize: '14px',
                                    transition: 'all 0.2s',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <button
                            type="submit"
                            style={{
                                backgroundColor: '#374151',
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '14px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                        >
                            Buscar
                        </button>
                    </form>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="bi bi-exclamation-triangle-fill" style={{ color: '#ef4444' }} />
                        <span style={{ color: '#991b1b', fontSize: '14px' }}>{error}</span>
                    </div>
                )}

                {/* Customers Table */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cliente</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cédula / RUC</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contacto</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ciudad</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tipo</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registro</th>
                                    <th style={{ padding: '14px 20px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ padding: '48px 24px', textAlign: 'center' }}>
                                            <i className="bi bi-people" style={{ fontSize: '40px', color: '#d1d5db', display: 'block', marginBottom: '12px' }} />
                                            <p style={{ color: '#6b7280', fontWeight: '600', fontSize: '16px', margin: 0 }}>
                                                {searchTerm ? 'No se encontraron clientes con el término buscado' : 'No hay clientes registrados'}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    customers.map(customer => {
                                        const typeStyle = getCustomerTypeColor(customer.customer_type);
                                        return (
                                            <tr
                                                key={customer.id}
                                                style={{ borderBottom: '1px solid #f3f4f6', transition: 'background-color 0.15s' }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <td style={{ padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            width: '38px',
                                                            height: '38px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#eff6ff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontWeight: '700',
                                                            color: '#2563eb',
                                                            fontSize: '13px',
                                                            flexShrink: 0
                                                        }}>
                                                            {(customer.first_name?.charAt(0) || 'C')}{(customer.last_name?.charAt(0) || '')}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: '700', color: '#111827', fontSize: '14px' }}>
                                                                {customer.first_name} {customer.last_name}
                                                                {customer.is_vip && (
                                                                    <span style={{ marginLeft: '6px', fontSize: '10px', backgroundColor: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontWeight: '800' }}>
                                                                        VIP
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                                                {customer.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                                    {customer.cedula || <span style={{ color: '#9ca3af' }}>-</span>}
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap', fontSize: '13px', color: '#374151' }}>
                                                    {customer.phone ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <i className="bi bi-telephone" style={{ color: '#9ca3af', fontSize: '12px' }} />
                                                            <span>{customer.phone}</span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#9ca3af' }}>Sin teléfono</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap', fontSize: '13px', color: '#4b5563' }}>
                                                    {customer.city || '-'}
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '20px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        backgroundColor: typeStyle.bg,
                                                        color: typeStyle.text,
                                                        border: `1px solid ${typeStyle.border}`,
                                                        display: 'inline-block',
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {customer.customer_type || 'Regular'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap', fontSize: '13px', color: '#6b7280' }}>
                                                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString('es-ES', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    }) : '-'}
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                                                        {/* Botón Detalles */}
                                                        <button
                                                            onClick={() => handleOpenDetails(customer)}
                                                            title="Ver detalles completos"
                                                            style={{
                                                                backgroundColor: '#eff6ff',
                                                                border: '1px solid #bfdbfe',
                                                                color: '#1d4ed8',
                                                                borderRadius: '6px',
                                                                padding: '6px 10px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                transition: 'all 0.15s'
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                                                        >
                                                            <i className="bi bi-eye-fill" />
                                                            <span>Detalles</span>
                                                        </button>

                                                        {/* Botón Editar */}
                                                        <button
                                                            onClick={() => handleOpenEdit(customer)}
                                                            title="Editar cliente"
                                                            style={{
                                                                backgroundColor: '#fef3c7',
                                                                border: '1px solid #fde68a',
                                                                color: '#b45309',
                                                                borderRadius: '6px',
                                                                padding: '6px 10px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                transition: 'all 0.15s'
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fde68a'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef3c7'; }}
                                                        >
                                                            <i className="bi bi-pencil-fill" />
                                                            <span>Editar</span>
                                                        </button>

                                                        {/* Botón Eliminar */}
                                                        {isAdmin && (
                                                            <button
                                                                onClick={() => handleDeleteCustomer(customer)}
                                                                title="Eliminar cliente permanentemente"
                                                                style={{
                                                                    backgroundColor: '#fee2e2',
                                                                    border: '1px solid #fecaca',
                                                                    color: '#dc2626',
                                                                    borderRadius: '6px',
                                                                    padding: '6px 10px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '700',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    transition: 'all 0.15s'
                                                                }}
                                                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fca5a5'; e.currentTarget.style.color = '#991b1b'; }}
                                                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                                                            >
                                                                <i className="bi bi-trash-fill" />
                                                                <span>Eliminar</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Stats */}
                {customers.length > 0 && (
                    <div style={{ marginTop: '20px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                            Mostrando <strong style={{ color: '#111827' }}>{customers.length}</strong> paciente{customers.length !== 1 ? 's' : ''} registrado{customers.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                )}
            </div>

            {/* ════════ MODAL: CREAR / EDITAR CLIENTE ════════ */}
            {showModal && (
                <div
                    onClick={closeModal}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050,
                        padding: '16px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '620px',
                            width: '100%',
                            maxHeight: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        {/* Header */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div>
                                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: 0 }}>
                                    {editingCustomerId ? 'Editar Paciente / Cliente' : 'Nuevo Paciente / Cliente'}
                                </h3>
                                <p style={{ color: '#6b7280', fontSize: '13px', margin: '4px 0 0 0' }}>
                                    {editingCustomerId ? 'Modifica los datos del cliente seleccionado' : 'Completa la información para registrar al cliente'}
                                </p>
                            </div>
                            <button
                                onClick={closeModal}
                                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>

                        {/* Body Form */}
                        <form onSubmit={handleSaveCustomer} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
                            <div style={{ padding: '24px', flex: 1 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Nombres *
                                        </label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={customerFormData.first_name}
                                            onChange={handleInputChange}
                                            required
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Apellidos *
                                        </label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={customerFormData.last_name}
                                            onChange={handleInputChange}
                                            required
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Cédula / RUC
                                        </label>
                                        <input
                                            type="text"
                                            name="cedula"
                                            value={customerFormData.cedula}
                                            onChange={handleInputChange}
                                            placeholder="10 o 13 dígitos"
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Teléfono *
                                        </label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={customerFormData.phone}
                                            onChange={handleInputChange}
                                            required
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                        Correo Electrónico *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={customerFormData.email}
                                        onChange={handleInputChange}
                                        required
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Ciudad
                                        </label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={customerFormData.city}
                                            onChange={handleInputChange}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Tipo de Cliente
                                        </label>
                                        <select
                                            name="customer_type"
                                            value={customerFormData.customer_type}
                                            onChange={handleInputChange}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                        >
                                            <option value="regular">Regular</option>
                                            <option value="vip">VIP</option>
                                            <option value="corporate">Corporativo</option>
                                            <option value="student">Estudiante</option>
                                            <option value="first_time">Primera Vez</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                        Dirección
                                    </label>
                                    <textarea
                                        name="address"
                                        rows={2}
                                        value={customerFormData.address}
                                        onChange={handleInputChange}
                                        placeholder="Calle principal, secundaria o referencia..."
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '20px', backgroundColor: '#f9fafb', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            name="is_vip"
                                            checked={customerFormData.is_vip}
                                            onChange={handleInputChange}
                                        />
                                        Cliente VIP
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={customerFormData.is_active}
                                            onChange={handleInputChange}
                                        />
                                        Activo
                                    </label>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={submitting}
                                    style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        backgroundColor: '#2563eb',
                                        color: 'white',
                                        padding: '9px 24px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)'
                                    }}
                                >
                                    {submitting ? 'Guardando...' : (editingCustomerId ? 'Actualizar Cliente' : 'Guardar Cliente')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ════════ MODAL: DETALLES DEL CLIENTE ════════ */}
            {showDetailsModal && selectedCustomer && (
                <div
                    onClick={closeDetailsModal}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050,
                        padding: '16px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '680px',
                            width: '100%',
                            maxHeight: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        {/* Header */}
                        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: '50%',
                                    backgroundColor: '#eff6ff',
                                    color: '#2563eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '18px',
                                    fontWeight: '800'
                                }}>
                                    {(selectedCustomer.first_name?.charAt(0) || 'C')}{(selectedCustomer.last_name?.charAt(0) || '')}
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {selectedCustomer.first_name} {selectedCustomer.last_name}
                                        {selectedCustomer.is_vip && (
                                            <span style={{ fontSize: '11px', backgroundColor: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '4px', fontWeight: '800' }}>
                                                VIP
                                            </span>
                                        )}
                                    </h3>
                                    <p style={{ color: '#6b7280', fontSize: '13px', margin: '3px 0 0 0' }}>
                                        {selectedCustomer.email} · Registrado el {selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeDetailsModal}
                                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>

                        {/* Body Details */}
                        <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
                            {loadingDetails ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>
                                    <p>Cargando información completa...</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Información de Contacto */}
                                    <div>
                                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                                            Información General y Contacto
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', background: '#f9fafb', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' }}>
                                            <div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block' }}>Cédula / RUC</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{selectedCustomer.cedula || 'No registrada'}</span>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block' }}>Teléfono</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{selectedCustomer.phone || 'No registrado'}</span>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block' }}>Ciudad</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{selectedCustomer.city || 'No especificada'}</span>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block' }}>Tipo de Cliente</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827', textTransform: 'capitalize' }}>{selectedCustomer.customer_type || 'Regular'}</span>
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block' }}>Dirección</span>
                                                <span style={{ fontSize: '13px', color: '#374151' }}>{selectedCustomer.address || 'Sin dirección registrada'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Estadísticas de Compra / Visitas */}
                                    <div>
                                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                                            Estadísticas & Actividad
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#15803d', textTransform: 'uppercase' }}>Total Gastado</div>
                                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#166534', marginTop: '4px' }}>
                                                    ${parseFloat(selectedCustomer.total_spent || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#1d4ed8', textTransform: 'uppercase' }}>Total Pedidos / Citas</div>
                                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e40af', marginTop: '4px' }}>
                                                    {selectedCustomer.total_orders || 0}
                                                </div>
                                            </div>
                                            <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#be185d', textTransform: 'uppercase' }}>Puntos Lealtad</div>
                                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#9d174d', marginTop: '4px' }}>
                                                    {selectedCustomer.loyalty?.points_balance || selectedCustomer.loyalty_points || 0} pts
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notas / Observaciones */}
                                    {selectedCustomer.notes && selectedCustomer.notes.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                                Notas Registradas
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {selectedCustomer.notes.map((n, idx) => (
                                                    <div key={idx} style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 14px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151' }}>
                                                        {n.content}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '16px 28px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: isAdmin ? 'space-between' : 'flex-end', alignItems: 'center', flexShrink: 0 }}>
                            {isAdmin && (
                                <button
                                    onClick={() => handleDeleteCustomer(selectedCustomer)}
                                    style={{
                                        padding: '9px 18px',
                                        borderRadius: '8px',
                                        border: '1px solid #fecaca',
                                        background: '#fee2e2',
                                        color: '#dc2626',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fca5a5'; e.currentTarget.style.color = '#991b1b'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                                >
                                    <i className="bi bi-trash-fill" />
                                    Eliminar Cliente
                                </button>
                            )}

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => handleOpenEdit(selectedCustomer)}
                                    style={{
                                        padding: '9px 18px',
                                        borderRadius: '8px',
                                        border: '1px solid #fde68a',
                                        background: '#fef3c7',
                                        color: '#b45309',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fde68a'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fef3c7'}
                                >
                                    <i className="bi bi-pencil-fill" />
                                    Editar
                                </button>
                                <button
                                    onClick={closeDetailsModal}
                                    style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default Clientes;